/**
 * Folder previews: which items peek out of a folder card, and where each
 * paper sits when the folder is at rest or fanned open.
 *
 * Pure functions so the geometry and grouping can be tested without a DOM.
 */
import type { Item } from '@/stores/canvasStore';
import { extractItemText } from '@/lib/itemTextExtractor';

/** Most papers a folder shows. More than this turns the fan into a deck. */
export const FOLDER_PREVIEW_MAX = 5;

const groupCache = new WeakMap<Item[], Map<string, Item[]>>();

/**
 * Items grouped by board, newest first, ghosts left out. Cached per
 * `allItems` array, so every folder on screen shares one pass over the list
 * instead of filtering thousands of items each.
 */
export function itemsByBoard(allItems: Item[]): Map<string, Item[]> {
  const cached = groupCache.get(allItems);
  if (cached) return cached;

  const groups = new Map<string, Item[]>();
  for (const item of allItems) {
    if (item.isGhost || !item.board_id) continue;
    const list = groups.get(item.board_id);
    if (list) list.push(item);
    else groups.set(item.board_id, [item]);
  }
  for (const list of groups.values()) {
    list.sort((a, b) => timeOf(b) - timeOf(a));
  }

  groupCache.set(allItems, groups);
  return groups;
}

const EMPTY: Item[] = [];

/** All items inside a board, newest first. */
export function boardContents(allItems: Item[], boardId: string | null | undefined): Item[] {
  if (!boardId) return EMPTY;
  return itemsByBoard(allItems).get(boardId) ?? EMPTY;
}

/** "Empty", "1 item", "12 items". */
export function folderSublabel(count: number): string {
  if (count <= 0) return 'Empty';
  return `${count} item${count === 1 ? '' : 's'}`;
}

function timeOf(item: Item): number {
  const t = Date.parse(item.updated_at || item.created_at);
  return Number.isNaN(t) ? 0 : t;
}

export interface PaperPose {
  x: number;
  y: number;
  rotate: number;
}

export interface FanOptions {
  /** Widest distance between the outer papers' centers, in px. */
  spread: number;
  /** How far the fan rises above the folder, in px. */
  lift: number;
  /** Rotation of the outer papers, in degrees. */
  tilt: number;
  paperWidth: number;
  paperHeight: number;
}

/** -1 for the leftmost paper, 1 for the rightmost, 0 for a lone paper. */
export function fanUnit(index: number, count: number): number {
  if (count <= 1) return 0;
  return (index / (count - 1)) * 2 - 1;
}

/**
 * Pose of a paper fanned out above the folder, relative to its rest slot.
 * Papers overlap their neighbours so the fan has no gaps for the pointer to
 * fall through, and the middle rides highest so the fan reads as an arc.
 */
export function fanPose(index: number, count: number, o: FanOptions): PaperPose {
  const u = fanUnit(index, count);
  const span = Math.min(o.spread, Math.max(0, count - 1) * o.paperWidth * 0.8);
  return {
    x: (u * span) / 2,
    y: -(o.paperHeight * 0.78 + o.lift) + u * u * o.lift * 0.7,
    rotate: u * o.tilt,
  };
}

/** Pose of a paper tucked in the folder: a loose stack, slightly splayed. */
export function restPose(index: number, count: number, paperWidth: number): PaperPose {
  const u = fanUnit(index, count);
  return {
    x: u * paperWidth * 0.12,
    y: Math.abs(u) * 2,
    rotate: u * 4,
  };
}

type Content = Record<string, unknown>;

const str = (v: unknown): string => (typeof v === 'string' ? v : '');
const stripHtml = (html: string) => html.replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ').trim();

/** Short, readable label for a paper: used as its text and its aria-label. */
export function paperLabel(item: Item): string {
  const c = (item.content ?? {}) as Content;
  switch (item.item_type) {
    case 'sticky':
      return str(c.text) || 'Sticky note';
    case 'text_card':
    case 'document':
      return str(c.title) || stripHtml(str(c.body)).slice(0, 80) || 'Untitled note';
    case 'image':
      return str(c.alt) || str(c.filename) || 'Image';
    case 'video':
      return str(c.title) || str(c.filename) || 'Video';
    case 'link':
      return str(c.title) || hostOf(str(c.url)) || 'Link';
    case 'file':
      return str(c.filename) || 'File';
    case 'board_link':
      return str(c.name) || str(c.boardName) || 'Folder';
    default:
      return extractItemText({ item_type: item.item_type, content: c }).slice(0, 80) || 'Item';
  }
}

export function hostOf(url: string): string {
  try {
    return new URL(url).hostname.replace(/^www\./, '');
  } catch {
    return '';
  }
}

/**
 * Canvas position for an item dropped at a screen point, centred on the
 * pointer. The folder's own box on screen maps screen px to canvas units, so
 * pan, zoom and the canvas's offset in the page are already accounted for.
 */
export function dropPosition(
  folder: { position_x: number; position_y: number; width: number },
  folderRect: { left: number; top: number; width: number },
  point: { clientX: number; clientY: number },
  size: { width: number; height: number },
): { x: number; y: number } {
  const unitsPerPx = folderRect.width > 0 ? folder.width / folderRect.width : 1;
  return {
    x: folder.position_x + (point.clientX - folderRect.left) * unitsPerPx - size.width / 2,
    y: folder.position_y + (point.clientY - folderRect.top) * unitsPerPx - size.height / 2,
  };
}
